/**
 * Sterling Forever — global scroll parallax utility.
 *
 * Applies to any element with `.sf-parallax`. Updates the CSS variable
 * `--sf-parallax-y` based on the element's position in the viewport so the
 * inner <img> translates slightly slower than the page scroll.
 *
 * Customization per instance:
 *   data-sf-parallax-intensity="12"   (default: 10 — percent of wrapper height)
 *
 * Behavior:
 *   - Bails on `prefers-reduced-motion: reduce`.
 *   - Only animates elements near the viewport (IntersectionObserver).
 *   - rAF-throttled scroll handler.
 *   - Auto-registers new elements on shopify:section:load and
 *     MutationObserver (for async inserts).
 */
(() => {
  if (typeof window === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const SELECTOR = '.sf-parallax';
  /** Scale delta. progress 0 (entering viewport) → scale = 1 + intensity; progress 1 (exiting) → scale = 1. */
  const DEFAULT_INTENSITY = 0.35;

  /**
   * Selectors for elements that should get the parallax effect automatically
   * because we can't edit their markup (Horizon core blocks rendered via
   * content_for). The JS tags them on DOMContentLoaded and on section reloads.
   */
  const AUTO_TAG_SELECTORS = [
    '.about-gallery .image-block',
    '.about-journey .image-block',
    '.about-rise .image-block',
    '.sf-big-image .image-block',
  ];

  /** @type {Set<HTMLElement>} */
  const activeSet = new Set();
  /** @type {WeakSet<HTMLElement>} */
  const registered = new WeakSet();
  let ticking = false;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = /** @type {HTMLElement} */ (entry.target);
        if (entry.isIntersecting) activeSet.add(el);
        else activeSet.delete(el);
      }
      if (activeSet.size) requestTick();
    },
    { rootMargin: '20% 0px' }
  );

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const update = () => {
    const vh = window.innerHeight;
    for (const el of activeSet) {
      const rect = el.getBoundingClientRect();
      const denom = vh + rect.height;
      if (denom <= 0) continue;
      // progress: 0 = entering from bottom, 1 = exiting from top.
      const progress = clamp((vh - rect.top) / denom, 0, 1);
      const intensity = parseFloat(el.dataset.sfParallaxIntensity) || DEFAULT_INTENSITY;
      // Scale: zoomed in while entering (1 + intensity), neutral while exiting (1).
      // Scroll down → progress grows → scale shrinks → image "recedes".
      // Scroll up   → progress shrinks → scale grows → image "approaches".
      const scale = 1 + intensity * (1 - progress);
      el.style.setProperty('--sf-parallax-scale', scale.toFixed(4));
    }
    ticking = false;
  };

  const requestTick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  /** @param {ParentNode} [root] */
  const autoTag = (root) => {
    if (!AUTO_TAG_SELECTORS.length) return;
    const scope = root || document;
    const joined = AUTO_TAG_SELECTORS.join(',');
    for (const node of scope.querySelectorAll(joined)) {
      if (node instanceof HTMLElement) node.classList.add('sf-parallax');
    }
  };

  /** @param {ParentNode} [root] */
  const register = (root) => {
    autoTag(root);
    const scope = root || document;
    const nodes = scope.querySelectorAll(SELECTOR);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement) || registered.has(node)) continue;
      registered.add(node);
      io.observe(node);
    }
  };

  /** @param {ParentNode} root */
  const unregister = (root) => {
    const nodes = root.querySelectorAll(SELECTOR);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      io.unobserve(node);
      activeSet.delete(node);
    }
  };

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick, { passive: true });

  // Theme editor: re-scan a section when it reloads, clean up on unload.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) register(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) unregister(event.target);
  });

  // Pick up dynamically inserted sections (quick-add, variant swaps, etc.).
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.(SELECTOR)) {
          if (!registered.has(node)) {
            registered.add(node);
            io.observe(node);
          }
        }
        if (node.querySelector?.(SELECTOR)) register(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  const init = () => register(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
